import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  ShieldAlert, RefreshCw, CheckCircle2, XCircle, Clock,
  Image, Search, Download,
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIT_EMAILS = ['nitinj@lsnminerals.com', 'vishnu@lsnminerals.com'];

const MODULE_LABELS = {
  diesel: 'Diesel',
  production: 'Production',
  maintenance: 'Maintenance',
  inventory: 'Store & Inventory',
  assets: 'Asset Management',
  tyres: 'Tyre Management',
  scrap: 'Scrap',
  tasks: 'Tasks',
  purchase_orders: 'Purchase Orders',
  parts_release: 'Parts Release',
};

const MODULE_COLORS = {
  diesel: 'bg-amber-100 text-amber-800',
  production: 'bg-blue-100 text-blue-800',
  maintenance: 'bg-purple-100 text-purple-800',
  inventory: 'bg-teal-100 text-teal-800',
  assets: 'bg-indigo-100 text-indigo-800',
  tyres: 'bg-orange-100 text-orange-800',
  scrap: 'bg-slate-100 text-slate-800',
  tasks: 'bg-pink-100 text-pink-800',
  purchase_orders: 'bg-green-100 text-green-800',
  parts_release: 'bg-cyan-100 text-cyan-800',
};

const StatusBadge = ({ status }) => {
  if (status === 'approved') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
      <CheckCircle2 className="h-3 w-3" /> Approved
    </span>
  );
  if (status === 'rejected') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      <XCircle className="h-3 w-3" /> Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
      <Clock className="h-3 w-3" /> Pending
    </span>
  );
};

export default function DeletionAuditLogPage({ user }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [proofUrl, setProofUrl] = useState(null);

  const canAccess = AUDIT_EMAILS.includes(user?.email);

  const fetchLog = async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (moduleFilter !== 'all') params.append('module', moduleFilter);
      const res = await axios.get(`${API}/delete-requests/audit-log?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(res.data);
    } catch (e) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(); }, [statusFilter, moduleFilter]);

  const filtered = records.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.record_display?.toLowerCase().includes(q) ||
      r.requested_by_email?.toLowerCase().includes(q) ||
      r.requested_by_name?.toLowerCase().includes(q) ||
      r.reason?.toLowerCase().includes(q) ||
      r.reviewed_by?.toLowerCase().includes(q)
    );
  });

  const stats = {
    total: records.length,
    pending: records.filter(r => r.status === 'pending').length,
    approved: records.filter(r => r.status === 'approved').length,
    rejected: records.filter(r => r.status === 'rejected').length,
  };

  const byModule = Object.keys(MODULE_LABELS).map(m => ({
    module: m,
    label: MODULE_LABELS[m],
    count: records.filter(r => r.module === m).length,
  })).filter(m => m.count > 0);

  const handleViewProof = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests/${reqId}/proof-image`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      setProofUrl(URL.createObjectURL(res.data));
    } catch { toast.error('No proof image available'); }
  };

  const exportCSV = () => {
    const headers = ['Date', 'Module', 'Record', 'Reason', 'Requested By', 'Status', 'Reviewed By', 'Review Date', 'Note'];
    const rows = filtered.map(r => [
      r.created_at ? format(new Date(r.created_at), 'yyyy-MM-dd HH:mm') : '',
      MODULE_LABELS[r.module] || r.module,
      r.record_display || r.record_id,
      r.reason || '',
      `${r.requested_by_name || ''} (${r.requested_by_email || ''})`,
      r.status,
      r.reviewed_by || '',
      r.reviewed_at ? format(new Date(r.reviewed_at), 'yyyy-MM-dd HH:mm') : '',
      r.review_note || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `deletion-audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (!canAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 gap-4" data-testid="audit-log-access-denied">
        <ShieldAlert className="h-16 w-16 text-slate-300" />
        <h2 className="text-xl font-semibold text-slate-600">Access Restricted</h2>
        <p className="text-slate-400 text-sm">This page is only accessible to authorised personnel.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="deletion-audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-red-500" />
            Global Deletion Audit Log
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Complete read-only record of all deletion requests across all modules</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchLog} data-testid="refresh-audit-log">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-audit-log">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Requests', value: stats.total, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Pending', value: stats.pending, color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'Approved & Deleted', value: stats.approved, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Rejected', value: stats.rejected, color: 'text-red-700', bg: 'bg-red-50' },
        ].map(s => (
          <Card key={s.label} className={`${s.bg} border-0 shadow-sm`}>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium">{s.label}</p>
              <p className={`text-3xl font-bold mt-1 ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* By Module breakdown */}
      {byModule.length > 0 && (
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-slate-600 font-semibold">Requests by Module</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex flex-wrap gap-2">
              {byModule.map(m => (
                <span key={m.module} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${MODULE_COLORS[m.module] || 'bg-slate-100 text-slate-700'}`}>
                  {m.label}
                  <span className="bg-white/50 rounded-full px-1.5 py-0.5 font-bold">{m.count}</span>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            placeholder="Search record, user, reason..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="audit-log-search"
          />
        </div>
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="w-44 h-9 text-sm" data-testid="audit-log-module-filter">
            <SelectValue placeholder="All Modules" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modules</SelectItem>
            {Object.entries(MODULE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="audit-log-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        {(moduleFilter !== 'all' || statusFilter !== 'all' || search) && (
          <Button variant="ghost" size="sm" onClick={() => { setModuleFilter('all'); setStatusFilter('all'); setSearch(''); }}>
            Clear Filters
          </Button>
        )}
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="text-xs font-semibold text-slate-600">Date</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600">Module</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600">Record</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600">Reason</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600">Requested By</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600">Reviewed By</TableHead>
              <TableHead className="text-xs font-semibold text-slate-600">Proof</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-400">
                  <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Loading audit log...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-400 text-sm">
                  No deletion requests found
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(r => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`audit-row-${r.id}`}>
                  <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                    {r.created_at ? format(new Date(r.created_at), 'dd MMM yyyy HH:mm') : '—'}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${MODULE_COLORS[r.module] || 'bg-slate-100 text-slate-700'}`}>
                      {MODULE_LABELS[r.module] || r.module}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700 max-w-[200px] truncate" title={r.record_display}>
                    {r.record_display || r.record_id}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[150px] truncate" title={r.reason}>
                    {r.reason || <span className="italic text-slate-300">No reason given</span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    <div>{r.requested_by_name || '—'}</div>
                    <div className="text-slate-400">{r.requested_by_email}</div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                    {r.status !== 'pending' && r.reviewed_at && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        {format(new Date(r.reviewed_at), 'dd MMM HH:mm')}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {r.reviewed_by ? (
                      <>
                        <div>{r.reviewed_by}</div>
                        {r.review_note && <div className="text-slate-400 italic truncate max-w-[120px]" title={r.review_note}>{r.review_note}</div>}
                      </>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {r.proof_image ? (
                      <Button variant="ghost" size="sm" onClick={() => handleViewProof(r.id)}
                        className="text-blue-500 hover:text-blue-700 h-7 px-2"
                        data-testid={`view-proof-${r.id}`}>
                        <Image className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Proof Image Dialog */}
      <Dialog open={!!proofUrl} onOpenChange={() => setProofUrl(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Proof of Deletion</DialogTitle>
          </DialogHeader>
          {proofUrl && <img src={proofUrl} alt="Proof" className="rounded-lg max-h-[60vh] object-contain mx-auto" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
